import {Typed} from "app/stimulus/typed-stimulus";
import {Controller} from "@hotwired/stimulus";
import {AsStimulusController} from "app/stimulus/as-stimulus-controller";
import {Component} from "@symfony/ux-live-component";
import {onLiveComponentRender} from "app/stimulus/live-component-render";
import {changeValue} from "app/utils/dom";

/**
 * Shows or hides the other limits and fills the bars under the required limits. The expanded state
 * is written to a hidden form field, so a surrounding live component renders the same state again.
 */
@AsStimulusController('card-limits-form')
export default class extends Typed(
    Controller<HTMLElement>, {
        targets: {
            optional: HTMLElement,
            optionalBody: HTMLElement,
            toggle: HTMLElement,
            chevron: HTMLElement,
            expanded: HTMLInputElement,
            meter: HTMLElement,
        },
    }
)
{
    private removeRenderListener: (() => void) | null = null;

    public connect(): void {
        this.removeRenderListener = onLiveComponentRender(this.element, (component) => {
            this.syncWithRender(component);
        });
    }

    public disconnect(): void {
        this.removeRenderListener?.();
        this.removeRenderListener = null;
    }

    public meter(event: Event): void {
        const input = event.target;
        if (input instanceof HTMLInputElement) {
            this.fillMeter(input);
        }
    }

    public toggle(): void {
        if (this.hasOptionalBodyTarget) {
            this.setExpanded(this.optionalBodyTarget.classList.contains('hidden'));
        }
    }

    /**
     * Live re-applies the classes changed here on top of every render, so they are set by the model again;
     * other limits with an error stay shown, so the error is visible.
     */
    private syncWithRender(component: Component): void {
        if (this.hasOptionalTarget && this.hasExpandedTarget) {
            const state = component.getData(this.expandedTarget.name);
            const hasError = this.optionalBodyTarget.querySelector('.form__errors') !== null;
            this.setExpanded(state === '1' || (state !== '0' && this.optionalTarget.dataset.defaultExpanded === 'true') || hasError);
        }
        this.meterTargets.forEach((bar) => {
            const input = document.getElementById(bar.dataset.input ?? '');
            if (input instanceof HTMLInputElement) {
                this.fillMeter(input);
            }
        });
    }

    private setExpanded(expanded: boolean): void {
        this.optionalBodyTarget.classList.toggle('hidden', !expanded);
        this.toggleTarget.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        this.chevronTarget.classList.toggle('rotate-180', expanded);
        if (this.hasExpandedTarget) {
            changeValue(this.expandedTarget, expanded ? '1' : '0');
        }
    }

    /**
     * Fills the bar under a required limit by the share of its maximum; an empty limit shows its group value.
     */
    private fillMeter(input: HTMLInputElement): void {
        const bar = this.meterTargets.find((target) => target.dataset.input === input.id);
        const maximum = Number(input.dataset.maximum);
        if (bar === undefined || !(maximum > 0)) {
            return;
        }
        const raw = input.value.trim() === '' ? (input.dataset.group ?? '') : input.value;
        const value = raw.trim() === '' ? 0 : Number(raw.replace(/[\s ]/g, '').replace(',', '.'));
        const share = Number.isFinite(value) ? Math.min(Math.max(value / maximum, 0), 1) : 0;
        bar.style.width = `${(share * 100).toFixed(1)}%`;
        bar.classList.toggle('bg-danger', Number.isFinite(value) && value > maximum);
    }
}
